import { useDispatch } from 'react-redux'
import { useSelector } from '@state/store'
import { useForm } from 'react-hook-form'

import { Button, Grid } from '@mui/material'
import { yupResolver } from '@hookform/resolvers/yup'

import { SelectInput } from '@components/inputs'
import { TextInput } from '@components/inputs'
import {
  WaitingRoom,
  RoomType,
  UpdateWaitingRoomDto,
  CreateWaitingRoomDto,
} from '@services/api'
import { MouseEventHandler, useEffect, useMemo, useState } from 'react'
import {
  createWaitingRoom,
  updateWaitingRoom,
} from '../../../../state/thunks/roomThunk'
import { addWaitingRoomSchema } from '../../../../utils/schemas'

type AddReceptionFormProps = {
  row?: WaitingRoom
  onClose?: () => void
  onCancelButtonPress?: (
    ev: MouseEventHandler<HTMLButtonElement> | undefined,
  ) => void
}

export function WaitingRoomsForm({ row, onClose }: AddReceptionFormProps) {
  const dispatch = useDispatch()
  const sites = useSelector(({ site }) => site.sites.datas)
  const rooms = useSelector(({ room }) => room.rooms)

  const [selectedSite, setSelectedSite] = useState<number | null>(null)

  const {
    formState: { errors },
    control,
    handleSubmit,
  } = useForm<WaitingRoom>({
    resolver: yupResolver(addWaitingRoomSchema),
    defaultValues: {
      ...row,
    },
  })

  useEffect(() => {
    setSelectedSite(row?.siteId || null)
  }, [row])

  const onSubmit = (values: WaitingRoom & { type?: RoomType }) => {
    if (!selectedSite) {
      return
    }
    if (row?.id) {
      const body: UpdateWaitingRoomDto = {
        code: values.code,
        label: values.label,
        siteId: selectedSite,
        receptionId: values.receptionId,
        capacity: values.capacity,
      }
      dispatch(updateWaitingRoom({ id: row.id, body }))
    } else {
      //add room
      const body: CreateWaitingRoomDto = {
        code: values.code,
        label: values.label,
        siteId: selectedSite,
        receptionId: values.receptionId,
        capacity: values.capacity,
      }
      dispatch(createWaitingRoom(body))
    }
    if (onClose) onClose()
  }

  const handleCancelPress = () => {
    if (onClose) {
      onClose()
    }
    // Closes the update form - Datagrid didnt provide a prop function to close form ()
    const el = (document as Document).querySelector(
      `.MuiDataGrid-row[data-id="${row?.id}"] .MuiIconButton-root.MuiIconButton-sizeSmall`,
    ) as any
    if (el) {
      el.click()
    }
  }

  const sitesOptions = useMemo(
    () =>
      sites.map((_site) => ({
        label: _site.label,
        value: _site.id,
      })),
    [sites],
  )
  const receptionOptions = useMemo(
    () =>
      rooms.reduce(
        (acc, _reception) => {
          if (
            _reception.type === RoomType.Reception &&
            _reception.siteId === selectedSite
          ) {
            acc.push({
              label: _reception.label,
              value: _reception.id,
            })
          }
          return acc
        },
        [] as {
          label: string
          value: number
        }[],
      ),
    [rooms, selectedSite],
  )

  const _handleSiteChange = (ev) => {
    setSelectedSite(ev.target.value)
  }

  return (
    <Grid container spacing={1} alignItems="flex-start">
      <Grid item xs={12} md={6}>
        <SelectInput
          value={selectedSite}
          name="siteId"
          variant="outlined"
          options={sitesOptions}
          onChange={_handleSiteChange}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <SelectInput
          options={receptionOptions}
          control={control}
          name="receptionId"
          errors={errors}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextInput name="code" errors={errors} control={control} />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextInput name="label" errors={errors} control={control} />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextInput
          name="capacity"
          type="number"
          InputProps={{
            inputProps: { min: 0 },
          }}
          errors={errors}
          control={control}
        />
      </Grid>

      <Grid container justifyContent="flex-end" gap={2} mt={2}>
        {onClose && (
          <Button onClick={handleCancelPress} variant="outlined">
            Annuler
          </Button>
        )}
        <Button
          onClick={handleSubmit(onSubmit)}
          variant="contained"
          color="secondary"
        >
          Sauvegarder
        </Button>
      </Grid>
    </Grid>
  )
}
